import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import {
  usePatientPackages,
  usePatientPackageConsumptions,
  type PatientPackageFinancialStatus,
  type PatientPackageSummary,
} from "@/hooks/usePatientPackages";
import { useDaycareHotelAccess } from "@/hooks/useDaycareHotelAccess";
import { usePermissionsMap } from "@/hooks/usePermissionsMap";
import { StayPlansSection } from "@/components/packages/StayPlansSection";

interface PatientPackagesPanelProps {
  ownerId: string;
  petId: string;
  clinicId: string;
  ownerName?: string | null;
  petName?: string | null;
}

const getDisplayLocale = (language: string) =>
  language.startsWith("pt") ? "pt-BR" : "en-US";

const formatCurrency = (value: number, language: string) =>
  new Intl.NumberFormat(getDisplayLocale(language), {
    style: "currency",
    currency: "BRL",
  }).format(value);

const formatDate = (value: string, language: string) =>
  new Intl.DateTimeFormat(getDisplayLocale(language)).format(new Date(value));

const formatDateTime = (value: string, language: string) =>
  new Intl.DateTimeFormat(getDisplayLocale(language), {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));

const getFinancialBadge = (status: PatientPackageFinancialStatus) => {
  if (status === "pago") return { labelKey: "packagePanel.paid", className: "bg-emerald-700 text-white" };
  if (status === "parcial") return { labelKey: "packagePanel.partial", className: "bg-amber-500 text-white" };
  return { labelKey: "packagePanel.pending", className: "bg-slate-600 text-white" };
};

function PackageConsumptions({ packageId }: { packageId: string }) {
  const { t, i18n } = useTranslation("patients");
  const { data: consumptions = [], isLoading } = usePatientPackageConsumptions(packageId);

  if (isLoading) return <Skeleton className="mt-4 h-16 w-full" />;

  return (
    <div className="mt-4 rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
      <p className="font-medium text-foreground">{t("packagesTab.consumptions")}</p>
      {consumptions.length === 0 ? (
        <p className="mt-1">{t("packagesTab.consumptionsEmpty")}</p>
      ) : (
        <ul className="mt-2 space-y-1">
          {consumptions.map((consumption) => (
            <li key={consumption.id}>
              {consumption.appointment?.starts_at
                ? formatDateTime(consumption.appointment.starts_at, i18n.language)
                : t("packagePanel.linkedToAppointment")}
              {consumption.sessions_to_consume > 1
                ? t("packagesTab.consumptionMultiplier", {
                    count: consumption.sessions_to_consume,
                  })
                : ""}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PatientPackagesPanel({
  ownerId,
  petId,
  clinicId,
  petName,
}: PatientPackagesPanelProps) {
  const { t, i18n } = useTranslation("patients");
  const { hasDaycare, hasHotel } = useDaycareHotelAccess();
  const { data: permissions } = usePermissionsMap();
  const { data: patientPackages = [], isLoading: isLoadingPatientPackages } = usePatientPackages(petId, clinicId, {
    includeInactive: true,
  });

  const canContractPlan = Boolean(permissions?.patients?.update) && (hasDaycare || hasHotel);

  const { activePackages, historyPackages } = useMemo(() => {
    const ordered = [...patientPackages].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

    return {
      activePackages: ordered.filter((pkg) => !pkg.catalog_package?.controls_sessions || pkg.sessions_remaining > 0),
      historyPackages: ordered.filter((pkg) => (pkg.catalog_package?.controls_sessions ?? true) && pkg.sessions_remaining <= 0),
    };
  }, [patientPackages]);

  const title = t("packagesTab.packagesSectionTitle");

  const renderPatientPackageCard = (pkg: PatientPackageSummary) => {
    const financialBadge = getFinancialBadge(pkg.status_financeiro);
    const controlsSessions = pkg.catalog_package?.controls_sessions ?? true;

    return (
      <div key={pkg.id} className="rounded-lg border border-border p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="text-base font-semibold text-foreground">
                {pkg.catalog_package?.item?.name ?? t("packagePanel.package")}
              </h4>
              <Badge className={financialBadge.className}>{t(financialBadge.labelKey)}</Badge>
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-3 text-sm md:grid-cols-3">
          <div className="grid gap-3 md:grid-cols-3">
            {controlsSessions ? (
              <>
                <div>
                  <p className="text-muted-foreground">{t("packagePanel.sessions")}</p>
                  <p className="font-medium">
                    {pkg.sessions_used}/{pkg.sessions_total} {t("packagePanel.usedSessions", { count: pkg.sessions_used })}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t("packagePanel.balance")}</p>
                  <p className="font-medium">{pkg.sessions_remaining}</p>
                </div>
              </>
            ) : (
              <div>
                <p className="text-muted-foreground">{t("packagePanel.sessions")}</p>
                <p className="font-medium">{t("packagePanel.noSessionControl")}</p>
              </div>
            )}
            <div>
              <p className="text-muted-foreground">{t("packagePanel.totalValue")}</p>
              <p className="font-medium">{formatCurrency(pkg.full_price, i18n.language)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t("packagePanel.amountPaid")}</p>
              <p className="font-medium">{formatCurrency(pkg.amount_paid, i18n.language)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t("packagePanel.outstanding")}</p>
              <p className="font-medium">{formatCurrency(pkg.outstanding_amount, i18n.language)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t("packagePanel.createdAt")}</p>
              <p className="font-medium">{formatDate(pkg.created_at, i18n.language)}</p>
            </div>
          </div>
        </div>

        {pkg.catalog_package?.controls_sessions !== false ? (
          <PackageConsumptions packageId={pkg.id} />
        ) : null}

        {pkg.origin === "appointment" ? (
          <div className="mt-4 rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">{t("packagePanel.appointmentOrigin")}</p>
            <p>
              {pkg.created_from_appointment?.starts_at
                ? formatDateTime(pkg.created_from_appointment.starts_at, i18n.language)
                : t("packagePanel.linkedToAppointment")}
            </p>
            <p className="mt-2">
              {t("packagePanel.editableOriginNotice")}
            </p>
          </div>
        ) : null}
      </div>
    );
  };

  const renderPackageList = (packages: PatientPackageSummary[], emptyLabel: string) => {
    if (packages.length === 0) {
      return (
        <div className="rounded-lg border border-border bg-white px-4 py-5 text-sm text-muted-foreground">
          {emptyLabel}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {packages.map((pkg) => renderPatientPackageCard(pkg))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>

        {isLoadingPatientPackages ? (
          <div className="space-y-2">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <div className="space-y-5">
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">{t("packagesTab.active")}</h4>
              {renderPackageList(activePackages, t("packagesTab.emptyPatientActive"))}
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">{t("packagesTab.history")}</h4>
              {renderPackageList(historyPackages, t("packagesTab.emptyPatientHistory"))}
            </div>
          </div>
        )}
      </section>

      {hasDaycare || hasHotel ? <StayPlansSection clinicId={clinicId} ownerId={ownerId} petId={petId} petName={petName ?? ""} hasDaycare={hasDaycare} hasHotel={hasHotel} canContract={canContractPlan} /> : null}
    </div>
  );
}
